import React from "react";
import {
  X,
  Check,
  Clock,
  AlertTriangle,
  Smartphone,
  Compass,
  MousePointerClick,
  Eye,
  Phone,
  Layers,
  Star,
  TrendingUp,
  RefreshCw,
  ArrowRight,
  CheckCircle2,
  Sparkles,
  MessageCircle,
  ShieldCheck,
  Server,
  Wrench,
  FileText,
  Wand2,
  Globe,
  HandCoins,
  Hourglass,
  Lock,
  HelpCircle,
} from "lucide-react";
import { OldBrowserMockup, BrowserMockup } from "@/components/site/Mockups";

/* ---------- Reusable section header ---------- */
const SectionHeader = ({
  eyebrow,
  title,
  lede,
  dark = false,
  center = true,
  accentColor = "text-blue-600",
  testid,
}) => (
  <div
    data-testid={testid}
    className={`${center ? "text-center" : ""} max-w-3xl ${center ? "mx-auto" : ""} fade-up`}
  >
    {eyebrow && (
      <div
        className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-black tracking-widest uppercase ${
          dark
            ? "bg-blue-500/10 text-blue-300 border border-blue-500/20"
            : "bg-blue-50 text-blue-700 border border-blue-100"
        }`}
      >
        <Sparkles size={12} className={accentColor} /> {eyebrow}
      </div>
    )}
    <h2
      className={`font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl mt-4 leading-tight ${
        dark ? "text-white" : "text-slate-900"
      }`}
    >
      {title}
    </h2>
    {lede && (
      <p
        className={`mt-5 text-base sm:text-lg ${
          dark ? "text-slate-300" : "text-slate-600"
        } leading-relaxed`}
      >
        {lede}
      </p>
    )}
  </div>
);

/* ============== PROBLEMS (with merged "I'll do it someday" intro) ============== */
export const ProblemsSection = () => {
  const problems = [
    {
      icon: Layers,
      title: "Old Design",
      body: "Your business may be great, but an outdated website can make people question your professionalism.",
    },
    {
      icon: Smartphone,
      title: "Poor Mobile Experience",
      body: "Most customers browse from their phones. If your site does not look good on mobile, you are losing trust fast.",
    },
    {
      icon: Compass,
      title: "Confusing Information",
      body: "If people cannot quickly understand what you offer, where you are, or how to contact you, they leave.",
    },
    {
      icon: MousePointerClick,
      title: "No Clear Call-To-Action",
      body: "Your website should guide visitors to call, message, or request a quote.",
    },
    {
      icon: Eye,
      title: "Weak First Impression",
      body: "Your website is your digital storefront. A weak one makes your business look smaller than it really is.",
    },
  ];
  return (
    <section
      id="problems"
      data-testid="problems-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionHeader
          eyebrow="The hidden cost"
          title="An Outdated Website Can Quietly Cost You Customers"
          lede="Most customers check your website before they call, visit, book, or buy. If it looks outdated, confusing, or unfinished, they may quietly choose a competitor before you ever get a chance to speak with them."
          testid="problems-header"
        />
        <p className="mt-6 text-center text-slate-600 max-w-2xl mx-auto fade-up">
          That is why so many business owners keep saying{" "}
          <span className="font-bold text-slate-900">“I’ll do it someday.”</span> But every day
          your website is missing or outdated, customers may be choosing someone else.
        </p>
        <div className="mt-12 fade-up">
          <img
            src="/home-2.png"
            alt="A business owner stressed at his desk looking at an outdated website — surrounded by problems like low trust, weak first impression, confusing layout, poor mobile experience, and low conversions"
            loading="lazy"
            className="w-full h-auto rounded-3xl shadow-[0_40px_120px_-30px_rgba(15,23,42,0.35)] ring-1 ring-slate-200"
          />
        </div>
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {problems.map((p, i) => {
            const Icon = p.icon;
            return (
              <div
                key={i}
                data-testid={`problem-card-${i}`}
                className="group rounded-3xl border border-slate-100 bg-white p-7 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all fade-up"
              >
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition">
                  <Icon size={22} />
                </span>
                <h3 className="mt-5 font-display text-xl font-bold text-slate-900">
                  {p.title}
                </h3>
                <p className="mt-2 text-slate-600 leading-relaxed">{p.body}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ============== OLD WAY VS NEW WAY ============== */
export const OldVsNewSection = () => {
  const oldWay = [
    "Search through dozens of web developers",
    "Compare confusing offers and prices",
    "Schedule meetings just to explain your business",
    "Try to describe a website you cannot see yet",
    "Pay before knowing how the final result will look",
    "Wait weeks or months for the first version",
    "Go back and forth while still running your business",
  ];
  const newWay = [
    "We review your business first",
    "For select businesses, we prepare a website concept upfront",
    "You see the vision before paying",
    "You approve it or request changes",
    "We finalize it quickly",
    "Once approved and access is ready, your site can be live the same day",
    "Hosting, updates, and support handled through the Care & Hosting Plan",
  ];
  return (
    <section
      id="old-vs-new"
      data-testid="old-vs-new-section"
      className="pt-28 md:pt-36 pb-24 md:pb-32 bg-slate-50"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionHeader
          eyebrow="The Old Way vs The New Way"
          title="The Old Way of Building a Website Takes Too Long"
          lede="Most business owners know they need a better website. The problem is not always the price. The real problem is the process."
          testid="old-vs-new-header"
        />
        <div className="mt-14 grid lg:grid-cols-2 gap-6 lg:gap-8 items-stretch">
          <div
            data-testid="old-way-card"
            className="h-full rounded-3xl border border-red-100 bg-gradient-to-b from-red-50/60 to-white p-7 lg:p-9 shadow-sm fade-up"
          >
            <div className="inline-flex items-center gap-2 rounded-full bg-red-100 text-red-700 px-3 py-1 text-xs font-black uppercase tracking-widest">
              <AlertTriangle size={14} /> The Usual Process
            </div>
            <ul className="mt-6 space-y-3.5">
              {oldWay.map((t, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-red-100 text-red-600 shrink-0">
                    <X size={14} />
                  </span>
                  <span className="text-slate-700">{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div
            data-testid="new-way-card"
            className="h-full rounded-3xl border border-emerald-100 bg-gradient-to-b from-emerald-50/60 to-white p-7 lg:p-9 shadow-sm fade-up"
          >
            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 text-emerald-700 px-3 py-1 text-xs font-black uppercase tracking-widest">
              <CheckCircle2 size={14} /> The Instant Site Revival Way
            </div>
            <ul className="mt-6 space-y-3.5">
              {newWay.map((t, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 shrink-0">
                    <Check size={14} />
                  </span>
                  <span className="text-slate-800 font-medium">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============== SEE THE CONCEPT FIRST (NEW) ============== */
export const SeeConceptSection = () => (
  <section
    id="see-concept"
    data-testid="see-concept-section"
    className="py-24 md:py-32 bg-[#0B0F19] text-white relative overflow-hidden"
  >
    <div className="absolute inset-0 hero-grid opacity-50 pointer-events-none" />
    <div className="absolute -top-40 right-0 h-96 w-96 rounded-full bg-yellow-400/15 blur-3xl pointer-events-none" />
    <div className="relative max-w-5xl mx-auto px-5 lg:px-8">
      <SectionHeader
        dark
        eyebrow="Low-risk advantage"
        title="No Guessing. No Waiting. See the Website Concept First."
        lede="Most web designers ask you to pay before you ever see what your website will look like. We do it the other way around."
        testid="see-concept-header"
      />
      <div className="mt-12 grid md:grid-cols-2 gap-6 fade-up">
        <div className="rounded-3xl glass-dark p-7 sm:p-9">
          <div className="inline-flex items-center gap-2 rounded-full bg-red-500/15 text-red-300 border border-red-500/30 px-3 py-1 text-xs font-black uppercase tracking-widest">
            <X size={12} /> The usual way
          </div>
          <ul className="mt-6 space-y-3 text-slate-300">
            {[
              "Pay upfront with no preview",
              "Wait weeks for a first draft",
              "Hope it looks anything like what you imagined",
              "Spend more time explaining than building",
            ].map((t, i) => (
              <li key={i} className="flex items-start gap-2">
                <X size={16} className="mt-1 text-red-400 shrink-0" />
                {t}
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-3xl glass-dark p-7 sm:p-9 ring-1 ring-yellow-400/30">
          <div className="inline-flex items-center gap-2 rounded-full bg-yellow-400/15 text-yellow-300 border border-yellow-400/30 px-3 py-1 text-xs font-black uppercase tracking-widest">
            <CheckCircle2 size={12} /> Our way
          </div>
          <ul className="mt-6 space-y-3 text-slate-200 font-medium">
            {[
              "We prepare a website concept for you upfront",
              "You review the vision before you pay",
              "You decide if it feels right for your business",
              "Once approved, we finalize and help launch it fast",
            ].map((t, i) => (
              <li key={i} className="flex items-start gap-2">
                <Check size={16} className="mt-1 text-emerald-400 shrink-0" />
                {t}
              </li>
            ))}
          </ul>
        </div>
      </div>
      <p className="mt-10 text-center text-slate-300 max-w-2xl mx-auto fade-up">
        For select businesses, we already have a concept ready before our first call. So instead
        of starting from zero, you start from{" "}
        <span className="text-yellow-300 font-bold">“is this what we’re launching?”</span>
      </p>
      <div className="mt-8 text-center fade-up">
        <a
          href="#contact"
          data-testid="see-concept-cta-btn"
          className="inline-flex items-center gap-2 rounded-full bg-yellow-400 hover:bg-yellow-300 text-slate-900 font-black px-7 py-4 transition-all hover:-translate-y-0.5 glow-gold"
        >
          Request My Website Review <ArrowRight size={18} />
        </a>
      </div>
    </div>
  </section>
);

/* ============== BEFORE & AFTER ============== */
export const BeforeAfterSection = () => {
  const before = [
    "Old-looking design",
    "Hard to navigate",
    "Not mobile-friendly",
    "Missing clear call-to-actions",
    "Customers leave confused",
  ];
  const after = [
    "Modern professional design",
    "Clear service presentation",
    "Mobile-optimized layout",
    "Strong buttons to call or book",
    "Business looks credible and ready",
  ];
  return (
    <section
      id="before-after"
      data-testid="before-after-section"
      className="py-24 md:py-32 bg-[#0B0F19] text-white relative overflow-hidden"
    >
      <div className="absolute -top-40 right-0 h-96 w-96 rounded-full bg-blue-600/15 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 left-0 h-96 w-96 rounded-full bg-yellow-500/10 blur-3xl pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <SectionHeader
          dark
          eyebrow="The transformation"
          title="From Outdated to Outstanding"
          lede="A quick visual of what a professional revival looks like — side by side."
          testid="before-after-header"
        />
        <div className="mt-12 fade-up">
          <img
            src="/home-3.png"
            alt="Outdated to outstanding — before and after transformation showing a cluttered, ineffective old website revived into a modern, clean, high-converting site in days, not months"
            loading="lazy"
            className="w-full h-auto rounded-3xl shadow-[0_40px_140px_-30px_rgba(59,130,246,0.35)] ring-1 ring-white/10"
          />
        </div>
        <div className="mt-14 grid lg:grid-cols-2 gap-8 items-stretch">
          <div data-testid="before-card" className="h-full flex flex-col rounded-3xl glass-dark p-6 sm:p-8 fade-up">
            <div className="inline-flex items-center gap-2 rounded-full bg-red-500/15 text-red-300 border border-red-500/30 px-3 py-1 text-xs font-black uppercase tracking-widest w-fit">
              <X size={12} /> Before
            </div>
            <div className="mt-5 flex-1 flex items-center justify-center">
              <div className="w-full transform sm:scale-[1.04]">
                <OldBrowserMockup />
              </div>
            </div>
            <ul className="mt-6 space-y-2.5">
              {before.map((t, i) => (
                <li key={i} className="flex items-start gap-2.5 text-slate-300">
                  <X size={16} className="mt-0.5 text-red-400 shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div data-testid="after-card" className="h-full flex flex-col rounded-3xl glass-dark p-6 sm:p-8 ring-1 ring-blue-500/30 fade-up">
            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 px-3 py-1 text-xs font-black uppercase tracking-widest w-fit">
              <Check size={12} /> After
            </div>
            <div className="mt-5 flex-1 flex items-center justify-center">
              <div className="w-full">
                <BrowserMockup />
              </div>
            </div>
            <ul className="mt-6 space-y-2.5">
              {after.map((t, i) => (
                <li key={i} className="flex items-start gap-2.5 text-slate-200 font-medium">
                  <Check size={16} className="mt-0.5 text-emerald-400 shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============== BENEFITS ============== */
export const BenefitsSection = () => {
  const benefits = [
    {
      icon: ShieldCheck,
      title: "Build Instant Trust",
      body: "A clean, modern website makes customers feel more confident choosing your business.",
    },
    {
      icon: Phone,
      title: "Get More Calls and Leads",
      body: "We design your site to make it easy for people to call, message, or request a quote.",
    },
    {
      icon: Eye,
      title: "Show What You Offer Clearly",
      body: "Your services, products, photos, reviews, and contact info — easy to find in seconds.",
    },
    {
      icon: Smartphone,
      title: "Look Great on Mobile",
      body: "Designed to look professional on phones, tablets, and desktops without a second thought.",
    },
    {
      icon: TrendingUp,
      title: "Stand Out From Competitors",
      body: "Most local businesses still have outdated websites. A modern site instantly raises your profile.",
    },
    {
      icon: Star,
      title: "Make Selling Easier",
      body: "When your site explains your business well, customers come in warmer and ready to buy.",
    },
  ];
  return (
    <section
      id="benefits"
      data-testid="benefits-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionHeader
          eyebrow="Why this matters"
          title="A Professional Website Makes Your Business Look Bigger and More Trustworthy"
          testid="benefits-header"
        />
        <div className="mt-12 fade-up">
          <img
            src="/home-4.png"
            alt="Modern websites built for local business — beautiful on every device (desktop, tablet, and mobile), mobile-first, fast and reliable, built to convert visitors into customers"
            loading="lazy"
            className="w-full h-auto rounded-3xl shadow-[0_40px_120px_-30px_rgba(15,23,42,0.2)] ring-1 ring-slate-200"
          />
        </div>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 items-stretch">
          {benefits.map((b, i) => {
            const Icon = b.icon;
            return (
              <div
                key={i}
                data-testid={`benefit-card-${i}`}
                className="group h-full flex flex-col rounded-3xl bg-white border border-slate-100 p-7 shadow-sm hover:shadow-xl transition-all fade-up"
              >
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-lg shrink-0">
                    <Icon size={20} />
                  </span>
                  <h3 className="font-display text-lg font-bold text-slate-900 leading-tight">{b.title}</h3>
                </div>
                <p className="mt-4 text-slate-600 leading-relaxed">{b.body}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ============== DIY WARNING (NEW) ============== */
export const DIYWarningSection = () => (
  <section
    id="diy-warning"
    data-testid="diy-warning-section"
    className="py-24 md:py-32 bg-slate-50"
  >
    <div className="max-w-5xl mx-auto px-5 lg:px-8">
      <SectionHeader
        eyebrow="The DIY trap"
        title="A DIY Website Still Costs You Time"
        testid="diy-warning-header"
      />
      <div className="mt-12 grid md:grid-cols-12 gap-6 items-center">
        <div className="md:col-span-7 fade-up">
          <p className="text-slate-700 leading-relaxed text-lg">
            Yes, business owners can try to build their own website. But most quickly realize it
            takes hours of writing, designing, editing, formatting, mobile testing, and figuring
            out technical settings.
          </p>
          <p className="mt-4 text-slate-700 leading-relaxed text-lg">
            That is time you could be spending running your business, serving customers, and
            making money.
          </p>
          <div className="mt-7 rounded-3xl bg-gradient-to-br from-yellow-300 via-yellow-400 to-amber-400 border-2 border-yellow-500/50 p-6 shadow-[0_20px_60px_-15px_rgba(250,204,21,0.6)] flex items-start gap-4">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-900 text-yellow-300 shrink-0 shadow-lg">
              <Sparkles size={22} />
            </span>
            <div>
              <div className="text-[11px] font-black uppercase tracking-widest text-slate-900/80">
                The Shortcut
              </div>
              <div className="mt-1 font-display text-xl sm:text-2xl font-extrabold text-slate-900 leading-snug">
                Instant Site Revival gives you the finished result without the headache.
              </div>
            </div>
          </div>
        </div>
        <div className="md:col-span-5 fade-up">
          <div className="rounded-3xl bg-white border border-slate-100 p-6 shadow-sm space-y-3">
            {[
              { icon: Hourglass, t: "Hours of writing & design" },
              { icon: Smartphone, t: "Mobile testing headaches" },
              { icon: Wrench, t: "Technical settings to figure out" },
              { icon: RefreshCw, t: "Constant updates to keep up" },
            ].map((row, i) => {
              const Ic = row.icon;
              return (
                <div
                  key={i}
                  className="flex items-center gap-3 rounded-2xl bg-slate-50 px-4 py-3"
                >
                  <span className="h-9 w-9 inline-flex items-center justify-center rounded-xl bg-white border border-slate-100 text-red-500">
                    <Ic size={18} />
                  </span>
                  <span className="text-slate-800 font-medium">{row.t}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ============== PROCESS ============== */
export const ProcessSection = () => {
  const steps = [
    {
      n: "01",
      title: "We Review Your Business",
      body: "We look at your current website, brand, services, competitors, and customer experience.",
    },
    {
      n: "02",
      title: "We Build Your Website Concept",
      body: "For select businesses, we create a professional concept upfront so you can see what your new website could look like.",
    },
    {
      n: "03",
      title: "You Review the Concept",
      body: "Instead of paying upfront blind, you review the concept and decide if it feels right for your business.",
    },
    {
      n: "04",
      title: "You Approve or Request Changes",
      body: "Tell us what you like, what to change, and what to adjust. We handle reasonable revisions before final approval.",
    },
    {
      n: "05",
      title: "We Finalize and Launch",
      body: "Once you approve it and we have the required access, your site can be live the same day.",
    },
  ];
  return (
    <section
      id="process"
      data-testid="process-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <SectionHeader
          eyebrow="How it works"
          title="The Easiest Website Process You’ll Ever Experience"
          testid="process-header"
        />
        <div className="mt-12 fade-up">
          <img
            src="/home-5.png"
            alt="How it works — the Instant Site Revival process: audit your current website, plan the new structure, build the modern website, launch, and ongoing support"
            loading="lazy"
            className="w-full h-auto rounded-3xl shadow-[0_40px_140px_-30px_rgba(15,23,42,0.4)] ring-1 ring-slate-800"
          />
        </div>
        <div className="mt-14 relative">
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-blue-200 via-blue-400 to-blue-200" />
          <ol className="space-y-6 md:space-y-12">
            {steps.map((s, i) => (
              <li
                key={i}
                data-testid={`process-step-${i}`}
                className="relative grid md:grid-cols-2 gap-6 items-center fade-up"
              >
                <div
                  className={`${i % 2 === 0 ? "md:order-1" : "md:order-2"} ${
                    i % 2 === 0 ? "md:pr-10 md:text-right" : "md:pl-10"
                  }`}
                >
                  <span className="inline-block font-display text-5xl font-black text-gradient-blue">
                    {s.n}
                  </span>
                  <h3 className="mt-2 font-display text-2xl font-bold text-slate-900">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-slate-600 leading-relaxed">{s.body}</p>
                </div>
                <div
                  className={`${
                    i % 2 === 0 ? "md:order-2" : "md:order-1"
                  } hidden md:flex justify-center`}
                >
                  <span className="h-6 w-6 rounded-full bg-blue-600 ring-8 ring-blue-100" />
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
};

/* ============== AFTER YOU SAY YES (NEW) ============== */
export const AfterYouSayYesSection = () => {
  const steps = [
    { icon: CheckCircle2, t: "You approve the website concept" },
    { icon: FileText, t: "You send any changes in writing" },
    { icon: Wand2, t: "We finalize the design" },
    { icon: Globe, t: "We connect your domain" },
    { icon: Sparkles, t: "Your website goes live" },
    { icon: RefreshCw, t: "We manage updates through your Care & Hosting Plan" },
  ];
  return (
    <section
      id="after-yes"
      data-testid="after-yes-section"
      className="py-24 md:py-32 bg-slate-50"
    >
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <SectionHeader
          eyebrow="What's next"
          title="What Happens After You Say Yes?"
          lede="A simple, controlled flow — no surprises, no confusing tech talk."
          testid="after-yes-header"
        />
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {steps.map((s, i) => {
            const Ic = s.icon;
            return (
              <div
                key={i}
                data-testid={`after-yes-step-${i}`}
                className="group h-full rounded-3xl bg-white border border-slate-100 p-5 sm:p-6 shadow-sm hover:shadow-xl transition fade-up flex items-start gap-4"
              >
                <span className="h-11 w-11 shrink-0 inline-flex items-center justify-center rounded-2xl bg-blue-600 text-white font-black">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="pt-0.5 min-w-0">
                  <div className="text-slate-500 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest">
                    <Ic size={12} /> Step {i + 1}
                  </div>
                  <div className="mt-1 font-display font-bold text-slate-900 text-base sm:text-lg leading-snug">
                    {s.t}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ============== PRICING ============== */
export const PricingSection = () => {
  const included = [
    "Fully designed professional landing page or small business website",
    "Modern homepage design",
    "Mobile-friendly responsive layout",
    "Clear service or product sections",
    "Call, text, email, or booking buttons",
    "Contact section + map area if needed",
    "Basic SEO-friendly page structure",
    "Review or testimonial section",
    "Social media links",
    "Professional copywriting structure",
    "Reasonable revisions before final approval",
    "Launch preparation support",
    "Same-day launch available after approval when info and access are ready",
  ];
  const notIncluded = [
    "Monthly hosting (covered by Care & Hosting Plan)",
    "Domain purchase or renewal fees",
    "Paid apps, plugins, or third-party software",
    "Advanced e-commerce store setup",
    "Complex booking systems unless quoted separately",
    "Logo design unless quoted separately",
    "Professional photography or video production",
    "Ongoing monthly updates without an active Care & Hosting Plan",
    "Paid advertising or marketing campaigns",
    "Multi-page websites beyond the agreed scope unless quoted separately",
  ];
  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="py-24 md:py-32 bg-[#0B0F19] text-white relative overflow-hidden"
    >
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 h-[500px] w-[500px] rounded-full bg-blue-600/20 blur-[140px] pointer-events-none" />
      <div className="absolute inset-0 hero-grid opacity-50 pointer-events-none" />
      <div className="relative max-w-5xl mx-auto px-5 lg:px-8">
        <SectionHeader
          dark
          eyebrow="Pricing"
          title="Professional Website Revival Package"
          lede="Designed for business owners who need a professional website without paying agency-level prices or waiting forever to launch."
          testid="pricing-header"
        />

        <div
          data-testid="pricing-card"
          className="mt-14 relative rounded-[2rem] glass-dark p-8 sm:p-12 ring-1 ring-yellow-400/30 shadow-[0_30px_120px_-20px_rgba(250,204,21,0.4)] fade-up"
        >
          <div className="absolute -top-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 rounded-full bg-yellow-400 text-slate-900 px-5 py-1.5 font-black text-xs tracking-widest uppercase shadow-[0_10px_30px_-5px_rgba(250,204,21,0.6)]">
            <Sparkles size={14} /> Limited-Time 50% OFF Special
          </div>

          <div className="grid lg:grid-cols-12 gap-10 mt-4">
            <div className="lg:col-span-5">
              <h3 className="font-display text-2xl sm:text-3xl font-extrabold">
                Website Revival Package
              </h3>
              <div className="mt-5 flex items-end gap-3">
                <span
                  data-testid="pricing-original"
                  className="text-2xl sm:text-3xl font-bold text-slate-500 line-through"
                >
                  $1,499
                </span>
                <span
                  data-testid="pricing-special"
                  className="font-display text-6xl sm:text-7xl font-black text-gradient-gold leading-none"
                >
                  $750
                </span>
              </div>
              <div className="mt-2 text-slate-300">one-time build</div>
              <div className="mt-1 text-slate-400 text-sm">
                + $99/month Website Care &amp; Hosting Plan (recommended)
              </div>

              <a
                href="#contact"
                data-testid="pricing-cta-btn"
                className="mt-7 inline-flex items-center justify-center gap-2 rounded-full bg-yellow-400 hover:bg-yellow-300 text-slate-900 font-black px-7 py-4 transition-all hover:-translate-y-0.5 glow-gold"
              >
                Claim My Website Revival Special <ArrowRight size={18} />
              </a>

              <div className="mt-6 flex items-start gap-2 text-xs text-slate-400 leading-relaxed">
                <HandCoins size={14} className="mt-0.5 shrink-0 text-yellow-400" />
                <p>
                  You own your website. Reasonable revisions during the build apply to design and
                  content changes before final approval. Major changes to the business model, full
                  redesigns after approval, or large new features may require a separate quote.
                </p>
              </div>
            </div>

            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-6">
              <div data-testid="pricing-included">
                <div className="text-emerald-300 font-black text-xs uppercase tracking-widest mb-3">
                  Included
                </div>
                <ul className="space-y-3">
                  {included.map((t, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-slate-100 text-[15px] leading-snug">
                      <Check size={16} className="mt-0.5 text-emerald-400 shrink-0" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div data-testid="pricing-not-included">
                <div className="text-red-300 font-black text-xs uppercase tracking-widest mb-3">
                  Not Included
                </div>
                <ul className="space-y-3">
                  {notIncluded.map((t, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-slate-300 text-[15px] leading-snug">
                      <X size={16} className="mt-0.5 text-red-400 shrink-0" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============== CARE & HOSTING PLAN ============== */
export const CarePlanSection = () => {
  const items = [
    { icon: Server, t: "Hosting" },
    { icon: Lock, t: "SSL / security basics" },
    { icon: HelpCircle, t: "Website support" },
    { icon: Wrench, t: "Minor updates" },
    { icon: FileText, t: "Text changes" },
    { icon: Eye, t: "Image changes" },
    { icon: Clock, t: "Business hours updates" },
    { icon: Sparkles, t: "Service updates" },
    { icon: Phone, t: "Contact info updates" },
    { icon: RefreshCw, t: "24 business-hour turnaround" },
  ];
  return (
    <section
      id="care-plan"
      data-testid="care-plan-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <SectionHeader
          eyebrow="Website Care & Hosting Plan"
          title="Keep Your Website Live, Updated, and Managed for $99/month"
          lede="The simple way to keep everything running, updated, supported, and ready for customers — without dealing with hosting accounts, technical issues, or backend settings."
          testid="care-plan-header"
        />
        <div className="mt-12 fade-up">
          <img
            src="/home-visual-b.png"
            alt="Website care dashboard preview — monthly traffic, call tracking, form submissions, mobile score, page speed, SEO basics, calls and forms trend, and ongoing optimization"
            loading="lazy"
            className="w-full h-auto rounded-3xl shadow-[0_40px_140px_-30px_rgba(15,23,42,0.45)] ring-1 ring-slate-800"
          />
          <p className="mt-4 text-center text-xs text-slate-500 max-w-2xl mx-auto italic leading-relaxed">
            Illustrative dashboard preview. Individual results vary — we focus on ongoing care, visibility,
            and a professional presence, not guaranteed traffic, ranking, or lead numbers.
          </p>
        </div>
        <div
          data-testid="care-plan-card"
          className="mt-14 grid lg:grid-cols-12 gap-8 rounded-[2rem] border border-slate-100 bg-gradient-to-br from-slate-50 to-white p-8 sm:p-10 shadow-xl fade-up"
        >
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 text-blue-700 border border-blue-100 px-3 py-1 text-xs font-black uppercase tracking-widest">
              <Server size={12} /> Care &amp; Hosting
            </div>
            <h3 className="mt-4 font-display text-3xl font-extrabold text-slate-900">
              Website Care &amp; Hosting Plan
            </h3>
            <div className="mt-5 flex items-baseline gap-2">
              <span
                data-testid="care-plan-price"
                className="font-display text-6xl font-black text-slate-900"
              >
                $99
              </span>
              <span className="text-slate-500 font-medium">/month</span>
            </div>
            <p className="mt-3 text-slate-600 leading-relaxed">
              Covers the ongoing costs and support needed to keep your website live, online,
              updated, and professionally managed.
            </p>
            <a
              href="#contact"
              data-testid="care-plan-cta-btn"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-bold px-7 py-4 transition-all hover:-translate-y-0.5 glow-blue"
            >
              Add Website Care &amp; Hosting <ArrowRight size={18} />
            </a>
            <div className="mt-6 rounded-2xl bg-blue-50 border border-blue-100 p-5">
              <div className="font-bold text-slate-900 flex items-center gap-2">
                <RefreshCw size={16} className="text-blue-600" /> How updates work
              </div>
              <p className="mt-2 text-sm text-slate-700 leading-relaxed">
                Send us your requested changes <span className="font-semibold">in writing</span>{" "}
                (email or message). We confirm details if needed and complete approved standard
                updates within <span className="font-semibold">24 business hours</span> or less.
              </p>
            </div>
            <div className="mt-4 rounded-2xl bg-slate-50 border border-slate-200 p-5">
              <div className="font-bold text-slate-900 flex items-center gap-2">
                <Lock size={16} className="text-slate-700" /> Cancel anytime
              </div>
              <p className="mt-2 text-sm text-slate-700 leading-relaxed">
                If you cancel, hosting, updates, and support stop, but we can discuss transfer
                options depending on your website setup.
              </p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="text-slate-900 font-black text-xs uppercase tracking-widest mb-4">
              Included
            </div>
            <ul className="grid sm:grid-cols-2 gap-3">
              {items.map((row, i) => {
                const Ic = row.icon;
                return (
                  <li
                    key={i}
                    className="flex items-center gap-3 rounded-2xl bg-white border border-slate-100 px-4 py-3"
                  >
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-600 shrink-0">
                      <Ic size={16} />
                    </span>
                    <span className="text-slate-800 text-sm font-medium">{row.t}</span>
                  </li>
                );
              })}
            </ul>
            <p className="mt-6 text-xs text-slate-500 leading-relaxed">
              Includes reasonable ongoing updates and support. Large redesigns, new pages, advanced
              features, e-commerce systems, booking systems, custom integrations, or major rebuilds
              may require a separate quote. You own your website — if you cancel the plan, hosting
              and support stop, but we can hand off the website files or transfer it to you.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============== INDUSTRIES (WHO THIS IS FOR) ============== */
export const IndustriesSection = () => {
  const tags = [
    "Restaurants", "Salons", "Barber Shops", "Med Spas", "Contractors",
    "Retail Stores", "Gyms & Studios", "Realtors", "Cellphone Repair",
    "Dentists & Doctors", "Auto Services", "Coaches & Consultants",
    "Home Services", "Local Experts",
  ];
  return (
    <section
      id="industries"
      data-testid="industries-section"
      className="py-24 md:py-32 bg-slate-50"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionHeader
          eyebrow="Who this is for"
          title="Built for Local Businesses Like Yours"
          lede="From restaurants and salons to contractors and med spas, we help local businesses look modern, professional, and ready for customers online."
          testid="industries-header"
        />
        <div className="mt-10 flex flex-wrap justify-center gap-2.5 fade-up">
          {tags.map((t, i) => (
            <span
              key={i}
              className="inline-flex items-center px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-800 font-medium shadow-sm hover:border-blue-400 hover:text-blue-700 hover:shadow-md transition"
            >
              {t}
            </span>
          ))}
        </div>
        <div className="mt-12 fade-up">
          <img
            src="/home-6.png"
            alt="Professional websites for local businesses that win — examples across restaurants, contractors, med spas, barber shops, hair salons, cellphone repair, gyms, realtors, dentists, and auto service"
            loading="lazy"
            className="w-full h-auto rounded-3xl shadow-[0_40px_120px_-30px_rgba(15,23,42,0.25)] ring-1 ring-slate-200"
          />
        </div>
      </div>
    </section>
  );
};

/* ============== TRUST CARDS (no fake testimonial) ============== */
export const TrustSection = () => {
  const cards = [
    { icon: HandCoins, t: "Clear pricing" },
    { icon: CheckCircle2, t: "Simple process" },
    { icon: Wand2, t: "No tech confusion" },
    { icon: Sparkles, t: "Same-day launch available" },
    { icon: RefreshCw, t: "Updates handled for you" },
    { icon: Eye, t: "See concept first" },
    { icon: ShieldCheck, t: "Built for small businesses" },
    { icon: Lock, t: "You own your website" },
  ];
  return (
    <section
      id="trust"
      data-testid="trust-section"
      className="py-24 md:py-32 bg-slate-50"
    >
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <SectionHeader
          eyebrow="Why business owners trust us"
          title="Built to Help Your Business Look Legit, Modern, and Ready for Customers"
          testid="trust-header"
        />
        <div className="mt-12 fade-up">
          <img
            src="/home-7.png"
            alt="Websites that build trust and drive local customers — every element works together: strong headline, service cards, customer reviews, location and map, before and after proof, call-now button, FAQs, and a strong call-to-action"
            loading="lazy"
            className="w-full h-auto rounded-3xl shadow-[0_40px_140px_-30px_rgba(15,23,42,0.35)] ring-1 ring-slate-800"
          />
        </div>
        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4 fade-up">
          {cards.map((c, i) => {
            const Ic = c.icon;
            return (
              <div
                key={i}
                data-testid={`trust-card-${i}`}
                className="rounded-3xl bg-white border border-slate-100 p-6 text-center hover:shadow-xl transition"
              >
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-600 text-white shadow">
                  <Ic size={20} />
                </span>
                <div className="mt-4 font-display font-bold text-slate-900">{c.t}</div>
              </div>
            );
          })}
        </div>
        <div className="mt-14 fade-up">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 text-blue-700 border border-blue-100 px-3 py-1 text-[11px] font-black tracking-widest uppercase">
              <Sparkles size={12} /> Everything we cover
            </div>
            <h3 className="font-display font-extrabold text-2xl sm:text-3xl mt-3 text-slate-900">
              The Full Website Revival Capability Set
            </h3>
          </div>
          <img
            src="/home-visual-a.png"
            alt="Website revival capabilities at a glance — outdated website fix, redesign, mobile responsive design, SEO basics, website speed, trust building, lead generation, call-to-action, local business support, analytics, ongoing support, and website care"
            loading="lazy"
            className="mt-8 w-full h-auto rounded-3xl shadow-[0_30px_90px_-20px_rgba(15,23,42,0.15)] ring-1 ring-slate-200 bg-gradient-to-br from-slate-50 to-white"
          />
        </div>
      </div>
    </section>
  );
};

/* ============== FINAL CTA ============== */
export const FinalCTASection = () => (
  <section
    id="final-cta"
    data-testid="final-cta-section"
    className="py-24 md:py-32 bg-[#0B0F19] text-white relative overflow-hidden"
  >
    <div className="absolute inset-0 hero-grid opacity-50 pointer-events-none" />
    <div className="absolute -top-32 left-1/2 -translate-x-1/2 h-[500px] w-[500px] rounded-full bg-yellow-400/15 blur-[140px] pointer-events-none" />
    <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
      <div className="lg:col-span-7 order-1 text-center lg:text-left fade-up">
        <div className="inline-flex items-center gap-2 rounded-full bg-yellow-400/10 border border-yellow-400/30 px-4 py-1.5 text-xs font-black tracking-widest uppercase text-yellow-300">
          <Sparkles size={14} /> Limited availability for select businesses
        </div>
        <h2 className="mt-5 font-display font-extrabold text-3xl sm:text-5xl lg:text-6xl leading-[1.05]">
          Stop Waiting for
          <span className="block text-gradient-gold mt-2">Someday.</span>
        </h2>
        <p className="mt-6 text-slate-300 max-w-2xl lg:mx-0 mx-auto">
          Your business deserves a website that makes people trust you before they ever call. Get a
          modern, mobile-friendly website built fast, launched quickly, and managed for you every
          month.
        </p>

        <div className="mt-9 inline-flex flex-wrap justify-center lg:justify-start items-end gap-x-6 gap-y-3 rounded-2xl glass-dark px-6 py-5">
          <div className="flex items-baseline gap-2">
            <span className="text-xs uppercase tracking-widest text-slate-400 font-bold">
              Website Build
            </span>
            <span className="text-lg font-bold text-slate-500 line-through">$1,499</span>
            <span className="font-display text-3xl sm:text-4xl font-black text-gradient-gold">
              $750
            </span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-xs uppercase tracking-widest text-slate-400 font-bold">
              Care &amp; Hosting
            </span>
            <span className="font-display text-2xl sm:text-3xl font-black text-white">$99</span>
            <span className="text-sm text-slate-400">/month</span>
          </div>
        </div>

        <p className="mt-4 text-sm text-slate-400 inline-flex items-center gap-2 justify-center lg:justify-start">
          <Clock size={14} className="text-blue-400" /> Same-day launch available after approval when
          all required information and access are ready.
        </p>

        <div className="mt-9 flex flex-wrap justify-center lg:justify-start gap-3">
          <a
            href="#contact"
            data-testid="final-cta-btn"
            className="inline-flex items-center gap-2 rounded-full bg-yellow-400 hover:bg-yellow-300 text-slate-900 font-black px-7 py-4 transition-all hover:-translate-y-0.5 glow-gold"
          >
            Request My Website Review <ArrowRight size={18} />
          </a>
          <a
            href="https://wa.me/17865756919?text=Hi%21%20I%27d%20like%20to%20learn%20about%20the%20website%20special."
            target="_blank"
            rel="noopener noreferrer"
            data-testid="final-cta-whatsapp-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1eb558] text-white font-bold px-7 py-4 transition-all hover:-translate-y-0.5"
          >
            <MessageCircle size={18} /> Message Us Now
          </a>
        </div>
      </div>

      <div className="lg:col-span-5 order-2 fade-up">
        <img
          src="/home-8.png"
          alt="See what's possible with your website — a free, no-obligation website audit to uncover opportunities, improve performance, and grow your local business online"
          loading="lazy"
          className="w-full h-auto rounded-3xl shadow-[0_40px_140px_-30px_rgba(250,204,21,0.3)] ring-1 ring-white/10"
        />
      </div>
    </div>
  </section>
);
